import React, { useState, useEffect, useMemo } from 'react';
import { WORKFLOW_STAGES, PRODUCT_TYPES, TIMELINE_COLORS } from '../../data/constants';
import ProductCard from './ProductCard';
import CalendarView from './CalendarView';
import SafeIcon from '../../common/SafeIcon';
import { 
  FiPlus, 
  FiTarget, 
  FiCalendar, 
  FiColumns, 
  FiShoppingBag, 
  FiTag, 
  FiClock, 
  FiDroplet 
} from 'react-icons/fi';

const WorkflowTab = ({ 
  products = [], 
  onAddProduct, 
  onUpdateProduct, 
  onDeleteProduct, 
  onMoveProduct, 
  targets, 
  setTargets, 
  designCounter,
  shops = [],
  activeShopId,
  protocols = []
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [viewMode, setViewMode] = useState('kanban');
  const [filterMode, setFilterMode] = useState('active');
  const [newProductForm, setNewProductForm] = useState({
    title: '',
    types: ['T-Shirt'],
    priority: 'Medium',
    startDate: new Date().toISOString().split('T')[0],
    targetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    timelineColor: TIMELINE_COLORS[0].value,
    shopIds: [],
    checklist: {}
  });

  useEffect(() => {
    if (activeShopId && newProductForm.shopIds.length === 0) {
      setNewProductForm(prev => ({ ...prev, shopIds: [activeShopId] }));
    }
  }, [activeShopId]);

  const addProduct = (e) => {
    if (e) e.preventDefault();
    if (!newProductForm.title.trim()) {
      alert("Please enter a design title first.");
      return;
    }

    const formattedCounter = String(designCounter).padStart(3, '0');
    const newProduct = {
      ...newProductForm,
      designNumber: `DSN-${formattedCounter}`,
      status: 'Ideas',
      createdAt: new Date().toISOString()
    };

    onAddProduct(newProduct);
    
    setNewProductForm({
      title: '',
      types: ['T-Shirt'],
      priority: 'Medium',
      startDate: new Date().toISOString().split('T')[0],
      targetDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      timelineColor: TIMELINE_COLORS[0].value,
      shopIds: activeShopId ? [activeShopId] : [],
      checklist: {}
    });
    setShowAddForm(false);
  };

  const filteredProducts = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    if (filterMode === 'all') return products;
    return products.filter(p => {
      if (!activeShopId) return true;
      return p.shopIds?.includes(activeShopId);
    });
  }, [products, filterMode, activeShopId]);

  const designsCompleted = useMemo(() => {
    return products.filter(p => 
      (p.status === 'Listed' || p.status === 'Live') && 
      (filterMode === 'all' || p.shopIds?.includes(activeShopId))
    ).length;
  }, [products, filterMode, activeShopId]);

  const handleShopToggle = (shopId) => {
    setNewProductForm(prev => {
      const ids = prev.shopIds.includes(shopId) 
        ? prev.shopIds.filter(id => id !== shopId) 
        : [...prev.shopIds, shopId];
      return { ...prev, shopIds: ids };
    });
  };

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 animate-fade-in space-y-12">
      <section>
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-sage text-white flex items-center justify-center font-serif font-bold text-lg">A</div>
            <h2 className="text-2xl text-espresso font-serif font-bold">Design Pipeline</h2>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-white p-1 rounded-xl border border-cream-dark shadow-sm">
              <button 
                onClick={() => setFilterMode('active')}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${filterMode === 'active' ? 'bg-terracotta text-white shadow-md' : 'text-espresso-light hover:bg-cream'}`}
              >
                Current Shop
              </button>
              <button 
                onClick={() => setFilterMode('all')}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${filterMode === 'all' ? 'bg-espresso text-white shadow-md' : 'text-espresso-light hover:bg-cream'}`}
              >
                All Shops
              </button>
            </div>

            <div className="flex bg-white p-1 rounded-xl border border-cream-dark shadow-sm">
              <button 
                onClick={() => setViewMode('kanban')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${viewMode === 'kanban' ? 'bg-espresso text-white shadow-md' : 'text-espresso-light hover:bg-cream'}`}
              >
                <SafeIcon icon={FiColumns} /> Board
              </button>
              <button 
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${viewMode === 'calendar' ? 'bg-espresso text-white shadow-md' : 'text-espresso-light hover:bg-cream'}`}
              >
                <SafeIcon icon={FiCalendar} /> Calendar
              </button>
            </div>

            <div className="flex gap-4 bg-white p-2.5 rounded-xl border border-cream-dark shadow-sm">
              <div className="flex items-center gap-2 pr-3 border-r border-cream-dark">
                <SafeIcon icon={FiTarget} className="text-terracotta w-4 h-4" />
                <div className="text-[10px] font-bold text-espresso-light uppercase">W: {designsCompleted}/{targets.weekly}</div>
              </div>
              <div className="text-[10px] font-bold text-espresso-light uppercase">M: {designsCompleted}/{targets.monthly}</div>
            </div>
          </div>
        </div>

        {viewMode === 'kanban' ? (
          <div className="bg-cream-dark/20 p-4 sm:p-6 rounded-3xl border border-cream-dark overflow-x-auto kanban-scroll">
            <div className="flex gap-6 min-w-max pb-4">
              {WORKFLOW_STAGES.map(stage => {
                const stageProducts = filteredProducts.filter(p => p.status === stage);
                const isIdeasStage = stage === 'Ideas';

                return (
                  <div key={stage} className="w-80 flex flex-col">
                    <div className="flex items-center justify-between mb-4 px-2">
                      <h3 className="font-bold text-espresso uppercase tracking-widest text-[11px]">{stage}</h3>
                      <span className="bg-white px-2 py-0.5 rounded-full text-[10px] font-bold text-espresso-light shadow-sm border border-cream-dark">
                        {stageProducts.length}
                      </span>
                    </div>

                    <div className="flex-1 bg-cream/30 rounded-2xl p-3 min-h-[550px] border border-cream-dark/50 space-y-4">
                      {isIdeasStage && (
                        <>
                          {showAddForm ? (
                            <div className="bg-white p-5 rounded-2xl border-2 border-terracotta shadow-xl animate-slide-up space-y-4 max-h-[580px] overflow-y-auto custom-scrollbar relative z-20">
                              <div>
                                <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-1 block">Design Title</label>
                                <input 
                                  autoFocus
                                  className="w-full text-sm font-bold border-b border-cream-dark pb-1 focus:outline-none focus:border-terracotta bg-transparent" 
                                  value={newProductForm.title}
                                  onChange={e => setNewProductForm({...newProductForm, title: e.target.value})}
                                  placeholder="e.g. Retro Cat design"
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-1 block">Product Type</label>
                                  <select 
                                    className="w-full text-[11px] font-bold bg-cream/30 p-2 rounded-lg border border-cream-dark focus:outline-none"
                                    value={newProductForm.types[0]}
                                    onChange={e => setNewProductForm({...newProductForm, types: [e.target.value]})}
                                  >
                                    {PRODUCT_TYPES.map(type => <option key={type}>{type}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-1 block">Priority</label>
                                  <select 
                                    className="w-full text-[11px] font-bold bg-cream/30 p-2 rounded-lg border border-cream-dark focus:outline-none"
                                    value={newProductForm.priority}
                                    onChange={e => setNewProductForm({...newProductForm, priority: e.target.value})}
                                  >
                                    <option>Low</option>
                                    <option>Medium</option>
                                    <option>High</option>
                                    <option>Urgent</option>
                                  </select>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-1 block">Start Date</label>
                                  <input 
                                    type="date" 
                                    className="w-full text-[11px] font-bold bg-cream/30 p-2 rounded-lg border border-cream-dark focus:outline-none"
                                    value={newProductForm.startDate}
                                    onChange={e => setNewProductForm({...newProductForm, startDate: e.target.value})}
                                  />
                                </div>
                                <div>
                                  <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-1 block">Deadline</label>
                                  <input 
                                    type="date" 
                                    className="w-full text-[11px] font-bold bg-cream/30 p-2 rounded-lg border border-cream-dark focus:outline-none"
                                    value={newProductForm.targetDate}
                                    onChange={e => setNewProductForm({...newProductForm, targetDate: e.target.value})}
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-2 block flex items-center gap-1">
                                  <SafeIcon icon={FiDroplet} className="w-2.5 h-2.5" /> Timeline Color
                                </label>
                                <div className="flex gap-2">
                                  {TIMELINE_COLORS.map(color => (
                                    <button 
                                      key={color.value}
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setNewProductForm({...newProductForm, timelineColor: color.value});
                                      }}
                                      className={`w-7 h-7 rounded-full border-2 transition-all ${newProductForm.timelineColor === color.value ? 'border-espresso scale-110 shadow-md ring-2 ring-white' : 'border-transparent'}`}
                                      style={{ backgroundColor: color.value }}
                                      title={color.name}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div>
                                <label className="text-[10px] font-black uppercase text-espresso-light/40 tracking-widest mb-2 block">Assign to Shops</label>
                                <div className="flex flex-wrap gap-2">
                                  {shops.map(shop => (
                                    <button 
                                      key={shop.id}
                                      type="button"
                                      onClick={() => handleShopToggle(shop.id)}
                                      className={`px-3 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${newProductForm.shopIds.includes(shop.id) ? 'bg-terracotta text-white border-terracotta' : 'bg-cream text-espresso-light/40 border-cream-dark hover:border-espresso-light'}`}
                                    >
                                      {shop.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setShowAddForm(false)} className="flex-1 py-2.5 text-espresso-light text-xs font-bold hover:bg-cream-dark rounded-xl transition-colors border border-transparent">Cancel</button>
                                <button type="button" onClick={addProduct} className="flex-1 py-2.5 bg-terracotta text-white text-xs font-bold rounded-xl hover:bg-terracotta/90 shadow-md transform active:scale-95 transition-all">Add Idea</button>
                              </div>
                            </div>
                          ) : (
                            <button 
                              onClick={() => setShowAddForm(true)}
                              className="w-full py-4 flex flex-col items-center justify-center gap-2 text-xs font-bold text-espresso-light/40 border-2 border-dashed border-cream-dark rounded-2xl hover:border-terracotta/40 hover:text-terracotta transition-all bg-white shadow-sm hover:shadow-md"
                            >
                              <SafeIcon icon={FiPlus} className="w-5 h-5" />
                              New Idea
                            </button>
                          )}
                        </>
                      )}

                      {stageProducts.map(product => (
                        <ProductCard 
                          key={product.id} 
                          product={product} 
                          updateProduct={onUpdateProduct}
                          deleteProduct={onDeleteProduct}
                          moveProduct={onMoveProduct}
                          shops={shops}
                          protocols={protocols}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <CalendarView products={filteredProducts} />
        )}
      </section>
    </div>
  );
};

export default WorkflowTab;